import React, { useState, useEffect, memo } from 'react';
import JanelaModal from '../../JanelaModal';
import { MAX_PARCELAS, dividirEmParcelas, vencimentosMensais } from '../../../data/invoices';
import { vencimentoDesdeATE, PRAZO_PADRAO, PRAZO_DESDE_A_TE } from '../../../utils/calculations/prazos';
import { addDays, MARK } from '../../../constants';
import { calcularEntrada, relogioDaTE } from '../../../utils/calculations/entrada';
import { FORMAS_DE_PAGAMENTO, FORMA_POR_ID, formaIncompleta } from '../../../constants';

/**
 * Que data o campo de vencimento traz pronta.
 *
 * Era HOJE, para as duas cobranças — o que significa "vence agora" e nasce
 * atrasada no dia seguinte. Nenhuma fatura é assim.
 *
 *   entrada            21 dias contados da TE, que é a regra do laboratório
 *   serviço que fecha
 *   na própria TE      os mesmos 21 dias da TE: sem entrada, é a fatura do
 *                      serviço que vence nesse prazo
 *   demais serviços    30 dias da emissão
 *
 * O caso do meio é o "por transferência", e ele estava recebendo 30 dias da
 * emissão. Como a fatura sai de sete a doze dias depois da TE, isso dava ao
 * cliente até 42 dias contados da transferência — o dobro do combinado.
 *
 * A data pode sair NO PASSADO quando a fatura demorou a ser emitida, e isso é
 * a verdade: o prazo conta da transferência, não da emissão. O campo continua
 * editável, e a tela avisa quando isso acontece.
 */
export const vencimentoSugerido = (service, modo, hoje) => {
  if (modo === 'entrada') {
    return vencimentoDesdeATE(service) || addDays(hoje, PRAZO_DESDE_A_TE);
  }
  if (relogioDaTE(service) === 'pagamento') {
    return vencimentoDesdeATE(service) || addDays(hoje, PRAZO_PADRAO);
  }
  return addDays(hoje, PRAZO_PADRAO);
};

const BillingModal = memo(({
  showModal,
  service,
  onConfirm,
  onClose,
  loading,
  calculateServiceValue,
  currentLab,
  clients,
  // O que a entrada JA RECEBEU neste servico. Nao muda o valor da fatura —
  // serve so para a tela dizer quanto vai sobrar de saldo depois dela.
  entradaPaga = 0,
  modo = 'servico',
  generateBillingPDF
}) => {
  const [billingData, setBillingData] = useState({
    billingDate: new Date().toISOString().split('T')[0],
    notes: '',
    formaPagamento: '',
    formaPagamentoObs: '',
    parcelas: 1,
    primeiroVencimento: new Date().toISOString().split('T')[0],
    // Datas escritas à mão, por número da parcela (da 2ª em diante; a 1ª é o
    // campo de cima). O mensal é sugestão: combinado com cliente raramente é
    // "todo dia 10", e a data que vale é a que foi combinada.
    datasDasParcelas: {},
  });

  useEffect(() => {
    if (showModal && service) {
      const hoje = new Date().toISOString().split('T')[0];
      setBillingData({
        billingDate: hoje,
        notes: '',
        formaPagamento: '',
        formaPagamentoObs: '',
        parcelas: 1,
        primeiroVencimento: vencimentoSugerido(service, modo, hoje),
        datasDasParcelas: {},
      });
    }
  }, [showModal, service, modo]);

  if (!showModal) return null;

  // Duas cobranças passam por aqui: a do serviço e a da ENTRADA. Reusar o
  // mesmo modal mantém parcelamento e conferência num lugar só, em vez de
  // duplicar a tela para mudar de onde vem o número.
  const ehEntrada = modo === 'entrada';

  const bruto = !service ? 0
    : ehEntrada ? calcularEntrada(service, currentLab).valor
    : calculateServiceValue(service);

  /**
   * A FATURA DO SERVIÇO SAI PELO PREÇO CHEIO.
   *
   * A entrada é adiantamento: ela cobra parte do mesmo serviço antes da hora,
   * e o que ela já recebeu abate no SALDO, não no valor do documento. Dez
   * embriões a R$ 150 são uma fatura de R$ 1.500 mesmo que R$ 950 de entrada
   * já tenham entrado; o que o cliente ainda deve é R$ 550.
   *
   * Eu tinha feito o contrário, descontando no documento: a fatura saía por
   * R$ 550 e o preço do serviço sumia do registro — com ele, a receita.
   */
  const valor = bruto;
  const saldoDepois = Math.max(0, Math.round((bruto - (Number(entradaPaga) || 0)) * 100) / 100);

  // Montado aqui e não no confirm: é o mesmo cálculo que vai ser gravado, e
  // quem fatura precisa ver os centavos ANTES de confirmar. A sobra cai na
  // primeira parcela, então 100 em 3x não são três de 33,33.
  //
  // A data escrita à mão vence a mensal. Mudar o primeiro vencimento recalcula
  // só as que ninguém tocou: apagar o que foi digitado em silêncio seria pior
  // que não deixar digitar.
  const mensais = vencimentosMensais(billingData.primeiroVencimento, billingData.parcelas);
  const parcelasPreview = valor > 0
    ? dividirEmParcelas(valor, billingData.parcelas).map((p, i) => ({
        ...p,
        vencimento: (p.numero > 1 && billingData.datasDasParcelas[p.numero] !== undefined)
          ? billingData.datasDasParcelas[p.numero]
          : mensais[i],
      }))
    : [];

  const mudarDataDaParcela = (numero, data) => {
    if (numero === 1) {
      setBillingData(prev => ({ ...prev, primeiroVencimento: data }));
      return;
    }
    setBillingData(prev => ({
      ...prev, datasDasParcelas: { ...prev.datasDasParcelas, [numero]: data },
    }));
  };

  // Fora de ordem não é erro — às vezes é o combinado —, mas quase sempre é
  // digitação, e a 3ª vencendo antes da 2ª confunde quem cobra.
  const foraDeOrdem = parcelasPreview.some((p, i) => i > 0
    && p.vencimento && parcelasPreview[i - 1].vencimento
    && p.vencimento < parcelasPreview[i - 1].vencimento);

  // "Outro" sem explicação produz uma fatura que não diz como foi paga — pior
  // que forma em branco, porque APARENTA estar preenchida.
  const faltaExplicar = formaIncompleta(billingData.formaPagamento, billingData.formaPagamentoObs);

  /**
   * Sem vencimento, a fatura nunca vence.
   *
   * O campo de data é apagável e nada o exigia. `vencimentosMensais` devolve
   * nulos quando ele vem vazio, a coluna aceita nulo, e o resultado é uma
   * cobrança que não entra em prazo nenhum: não aparece como próxima a vencer,
   * não conta como vencida, não vira criticidade. Dinheiro invisível — que é
   * exatamente o que o arquivo de prazos existe para evitar.
   */
  const faltaVencimento = !billingData.primeiroVencimento
    || parcelasPreview.some(p => !p.vencimento);
  const naoPodeConfirmar = faltaExplicar || faltaVencimento || valor <= 0;

  const handleConfirm = () => {
    if (naoPodeConfirmar) return;
    onConfirm({ ...billingData, parcelasCalculadas: parcelasPreview, modo });
  };

  const generateModalPDF = () => {
    generateBillingPDF(service, currentLab, clients, {
      ...billingData,
      // O gerador não sabia qual das duas cobranças estava imprimindo, e saía
      // sempre como fatura do serviço: título, detalhamento do modelo e custos
      // operacionais, com as parcelas da entrada embaixo.
      modo,
      parcelas: parcelasPreview,
      // Só aqui o abatimento é conhecido com certeza: é a conta que acabou de
      // ser feita. Num PDF regerado depois não dá para reconstruir o bruto a
      // partir do líquido já gravado, e inventar o número seria pior.
      entradaAbatida: 0,
    });
  };

  const handleConfirmWithPDF = () => {
    generateModalPDF();
    handleConfirm();
  };

  return (
    <JanelaModal chave="faturamento" titulo="Confirmar Faturamento" onFechar={onClose}
      largura="sm:max-w-md">
      <div className="p-4 sm:p-6">

        <div className="space-y-4">
          <div>
            <p className="text-sm font-medium text-slate-700">Cliente:</p>
            <p className="text-base font-medium text-slate-900">{service?.client_name}</p>
          </div>

          <div>
            <p className="text-sm font-medium text-slate-700">
              {ehEntrada ? 'Entrada a Cobrar:' : 'Valor a Faturar:'}
            </p>

            <p className="text-xl font-bold text-slate-900 tabular-nums">
              R$ {valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </p>

            {/* O adiantamento já recebido, dito aqui porque é o que muda o
                que o cliente ainda deve — e não o que está no papel. */}
            {!ehEntrada && entradaPaga > 0 && (
              <p className="text-xs text-slate-600 mt-1">
                A entrada já recebeu R$ {entradaPaga.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}.
                {saldoDepois > 0
                  ? ` Depois desta fatura o cliente fica devendo R$ ${saldoDepois.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}.`
                  : ' Esta fatura já fica coberta pelo que entrou.'}
              </p>
            )}

            {/* A entrada passou a abater em TODOS os modelos, pelo valor
                FATURADO. A exclusao que existia em `por_te` produzia o cobrado
                em dobro que ela queria evitar: a fatura da entrada ficava
                aberta e o bruto do servico vinha inteiro por cima. */}

          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Data de Faturamento
            </label>
            <input
              type="date"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={billingData.billingDate}
              onChange={(e) => setBillingData(prev => ({ ...prev, billingDate: e.target.value }))}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Parcelamento
            </label>
            <div className="flex gap-2">
              {Array.from({ length: MAX_PARCELAS }, (_, i) => i + 1).map(n => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setBillingData(prev => ({ ...prev, parcelas: n, datasDasParcelas: {} }))}
                  className={`flex-1 py-2 rounded-lg border text-sm font-medium ${
                    billingData.parcelas === n
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-slate-300 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {n}x
                </button>
              ))}
            </div>
          </div>

          {/* Sempre visível. Aparecia só com mais de uma parcela, e a fatura de
              parcela única — que é a maioria — ficava com um vencimento que
              ninguém via nem podia mudar, ainda que ele fosse gravado. */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              {billingData.parcelas > 1 ? 'Primeiro vencimento' : 'Vencimento'}
            </label>
            <input
              type="date"
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                faltaVencimento ? 'border-slate-500' : 'border-slate-300'}`}
              value={billingData.primeiroVencimento}
              onChange={(e) => setBillingData(prev => ({ ...prev, primeiroVencimento: e.target.value }))}
            />
            {faltaVencimento && (
              <p className="text-xs text-slate-700 mt-1">
                {MARK.WARN} Informe o vencimento. Sem ele a fatura não entra em prazo nenhum:
                não aparece como próxima a vencer, nem como vencida.
              </p>
            )}
            {(() => {
              const hoje = new Date().toISOString().split('T')[0];
              const venc = billingData.primeiroVencimento;
              if (venc && venc < hoje) {
                return (
                  <p className="text-xs text-slate-700 mt-1">
                    {MARK.WARN} Esta data já passou — a fatura nasce vencida.
                    {(ehEntrada || relogioDaTE(service) === 'pagamento')
                      && ' O prazo conta da TE, não da emissão.'}
                  </p>
                );
              }
              return (
                <p className="text-xs text-slate-500 mt-1">
                  {ehEntrada || relogioDaTE(service) === 'pagamento'
                    ? `${PRAZO_DESDE_A_TE} dias contados da TE.`
                    : `${PRAZO_PADRAO} dias da emissão.`} Depois desta data o serviço entra como crítico.
                </p>
              );
            })()}
          </div>

          {/* Cada parcela com a SUA data. Vinha pronta, mês a mês a partir da
              primeira, e só a primeira se mexia. A 1ª daqui é o mesmo campo
              de cima, escrito nos dois lugares. */}
          {parcelasPreview.length > 1 && (
            <div className="bg-slate-50 rounded-lg p-3 space-y-1.5">
              {parcelasPreview.map(p => (
                <div key={p.numero} className="flex items-center justify-between gap-2 text-sm">
                  <span className="text-slate-600 shrink-0">{p.numero}ª</span>
                  <input
                    type="date"
                    aria-label={`Vencimento da ${p.numero}ª parcela`}
                    className={`min-w-0 flex-1 px-2 py-1 border rounded text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      p.vencimento ? 'border-slate-300' : 'border-slate-500'}`}
                    value={p.vencimento || ''}
                    onChange={(e) => mudarDataDaParcela(p.numero, e.target.value)}
                  />
                  <span className="font-medium tabular-nums shrink-0">
                    R$ {p.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              ))}
              {foraDeOrdem && (
                <p className="text-xs text-slate-700 pt-1">
                  {MARK.WARN} Há parcela vencendo antes da anterior. Confira se é isso mesmo.
                </p>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Forma de pagamento
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <select
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={billingData.formaPagamento}
                onChange={(e) => setBillingData(prev => ({ ...prev, formaPagamento: e.target.value }))}
              >
                <option value="">Não informada</option>
                {FORMAS_DE_PAGAMENTO.map(f => (
                  <option key={f.id} value={f.id}>{f.label}</option>
                ))}
              </select>
              {/* O complemento aparece só depois de escolher a forma: vazio ao
                  lado de "Não informada" seria um campo pedindo nada. */}
              {billingData.formaPagamento && (
                <input
                  type="text"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    faltaExplicar ? 'border-slate-500' : 'border-slate-300'}`}
                  placeholder={FORMA_POR_ID[billingData.formaPagamento]?.dica || ''}
                  value={billingData.formaPagamentoObs}
                  onChange={(e) => setBillingData(prev => ({ ...prev, formaPagamentoObs: e.target.value }))}
                />
              )}
            </div>
            {faltaExplicar && (
              <p className="text-xs text-slate-600 mt-1">
                Descreva a forma — "Outro" sozinho não diz como a fatura foi paga.
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Observações (opcional)
            </label>
            <textarea
              rows="3"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Observações sobre o faturamento..."
              value={billingData.notes}
              onChange={(e) => setBillingData(prev => ({ ...prev, notes: e.target.value }))}
            />
          </div>

          <div className="text-xs text-slate-500 bg-blue-50 p-3 rounded-lg">
            Um PDF com o resumo será gerado automaticamente após a confirmação.
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={handleConfirmWithPDF}
            disabled={loading || naoPodeConfirmar}
            className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
          >
            {loading ? 'Faturando...' : 'Confirmar e Gerar PDF'}
          </button>
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 bg-slate-500 text-white rounded-lg hover:bg-slate-600 disabled:opacity-50"
          >
            Cancelar
          </button>
        </div>
      </div>
    </JanelaModal>
  );
});

export default BillingModal;
